/**
 * /auth/callback — receives Dex/Google's redirect after the user signs in.
 *
 * URL: /auth/callback?code=<code>&state=<state>
 *
 * Behavior:
 *   1. Read code + state from URL
 *   2. Verify state matches the one stashed in sessionStorage (CSRF)
 *   3. POST { code, redirect_uri } to /auth/google → cookie is set
 *   4. On 200: invalidate useMe query + navigate to /dashboard
 *   5. On 401 / 403: render error with retry link to /login
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@nu-assist/ui";
import { RETURN_TO_KEY } from "../../shared/AuthGuard";
import { MEDIA_SESSION_QUERY_KEY } from "../../features/media/MediaSessionProvider";

const STATE_KEY = "nu-assist:oauth-state";
const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

type CallbackError =
  | { kind: "csrf" }
  | { kind: "missing-code" }
  | { kind: "google-rejected"; detail: string }
  | { kind: "forbidden"; detail: string }
  | { kind: "config"; detail: string }
  | { kind: "network"; detail: string };

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<CallbackError | null>(null);

  // React StrictMode runs effects twice in development (mount → unmount →
  // mount) to surface bugs in cleanup. OAuth codes are single-use: the
  // second POST would always fail with Google's `invalid_grant` even
  // though the first succeeded. Track which code we've already POSTed
  // and short-circuit the second invocation. The ref survives the
  // StrictMode re-mount (same component instance).
  const consumedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const code = params.get("code");
    const returnedState = params.get("state");

    if (code && consumedCodeRef.current === code) {
      // StrictMode double-invocation — first run already POSTed this
      // code and either navigated (success) or set an error.
      return;
    }
    if (code) {
      consumedCodeRef.current = code;
    }
    const expectedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);

    if (!code) {
      setError({ kind: "missing-code" });
      return;
    }
    if (!returnedState || returnedState !== expectedState) {
      setError({ kind: "csrf" });
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;
    fetch(`${API_BASE}/auth/google`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
      .then(async (response) => {
        if (response.ok) {
          // Cookie is set on .{stage}.nu-assist.com — invalidate useMe so
          // <AuthGuard /> sees the authenticated state on next render.
          await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
          // Return-to-after-login: land on the deep link the user
          // originally tried to reach (stored by AuthGuard before the
          // /login bounce); fall back to /dashboard when none. Survives
          // the OAuth round-trip via sessionStorage.
          const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
          sessionStorage.removeItem(RETURN_TO_KEY);
          navigate(returnTo?.startsWith("/") ? returnTo : "/dashboard", {
            replace: true,
          });
          // The media session is not asked for on /auth/callback (see
          // MediaSessionProvider); ask now that this page has been left, so
          // logos and avatars move to CloudFront without a reload.
          void queryClient.resetQueries({ queryKey: MEDIA_SESSION_QUERY_KEY });
          return;
        }
        const body = await response.json().catch(() => ({ detail: "" }));
        const detail = String(body.detail ?? "");
        if (response.status === 403) {
          setError({ kind: "forbidden", detail });
        } else if (response.status === 503) {
          setError({ kind: "config", detail });
        } else if (response.status === 401) {
          setError({ kind: "google-rejected", detail });
        } else {
          setError({
            kind: "network",
            detail: `${response.status} ${response.statusText}`,
          });
        }
      })
      .catch((err) => {
        setError({
          kind: "network",
          detail: err instanceof Error ? err.message : String(err),
        });
      });
  }, [params, navigate, queryClient]);

  if (!error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-4 font-sans text-text">
        <h1 className="sr-only">Signing in</h1>
        <p className="text-body">Signing you in…</p>
      </div>
    );
  }

  const messages: Record<CallbackError["kind"], string> = {
    csrf: "Security check failed (CSRF). Please try logging in again.",
    "missing-code": "Sign-in returned without a code. Please try again.",
    "google-rejected": "The sign-in provider rejected the code. Please try again.",
    // Fallback: only shown if a 403 arrives with no detail (e.g. a raw
    // proxy 403 before reaching the app layer). Normal app 403s carry detail.
    forbidden: "Login failed.",
    config: "Auth backend not yet configured. Contact ops or see /health/auth-config.",
    network: "Couldn't reach the api. Please retry.",
  };

  const displayMessage =
    error.kind === "forbidden" && error.detail ? error.detail : messages[error.kind];

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4 font-sans text-text">
      <div className="w-full max-w-sm">
        <Card>
          <div role="alert" data-testid="auth-callback-error">
            <h1 className="text-h1 text-error mb-3">Login failed</h1>
            <p className="mb-4 text-body text-text-secondary" data-testid="auth-error-message">
              {displayMessage}
            </p>
            {"detail" in error && error.detail && error.kind !== "forbidden" ? (
              <details className="mb-4 text-caption text-text-secondary">
                <summary>Details</summary>
                <code>{error.detail}</code>
              </details>
            ) : null}
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-accent text-bg text-label no-underline transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              Try again
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
